import { logger } from './logger';

// Cache configuration
const CACHE_VERSION = 'v1.0.0';
const STORAGE_KEY = 'library-cache-metadata';

// Essential libraries configuration
interface LibraryConfig {
  name: string;
  importFunction: () => Promise<unknown>;
  size: number; // Estimated size in bytes
  priority: 'high' | 'medium' | 'low';
}

const ESSENTIAL_LIBRARIES: LibraryConfig[] = [
  {
    name: 'qrcode.react',
    importFunction: () => import('qrcode.react'),
    size: 150000, // ~150KB
    priority: 'high',
  },
  {
    name: 'qr-code-styling',
    importFunction: () => import('qr-code-styling'),
    size: 200000, // ~200KB
    priority: 'high',
  },
];

// Storage utilities
class StorageManager {
  static async getStorageEstimate(): Promise<{ quota: number; usage: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        quota: estimate.quota || 0,
        usage: estimate.usage || 0,
      };
    }
    return { quota: 0, usage: 0 };
  }

  static async hasEnoughSpace(requiredBytes: number): Promise<boolean> {
    try {
      const { quota, usage } = await this.getStorageEstimate();
      const available = quota - usage;
      const buffer = 50 * 1024 * 1024; // 50MB buffer

      return available > requiredBytes + buffer;
    } catch (error) {
      logger.warn('Could not check storage space:', error);
      return true; // Assume we have space if we can't check
    }
  }

  static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Cache manager
export class LibraryCacheManager {
  private static instance: LibraryCacheManager;
  private cache: Map<string, unknown> = new Map();
  private downloadListeners: Set<(progress: CacheProgress) => void> = new Set();

  static getInstance(): LibraryCacheManager {
    if (!LibraryCacheManager.instance) {
      LibraryCacheManager.instance = new LibraryCacheManager();
    }
    return LibraryCacheManager.instance;
  }

  // Check if cache version is outdated
  needsUpdate(): boolean {
    const cachedVersion = localStorage.getItem(STORAGE_KEY);
    if (!cachedVersion) {
      return false;
    }
    return cachedVersion !== CACHE_VERSION;
  }

  // Get total size of libraries to download
  getTotalDownloadSize(): number {
    return ESSENTIAL_LIBRARIES.reduce((total, lib) => total + lib.size, 0);
  }

  // Add download progress listener
  onDownloadProgress(callback: (progress: CacheProgress) => void): () => void {
    this.downloadListeners.add(callback);
    return () => this.downloadListeners.delete(callback);
  }

  // Notify download progress
  private notifyProgress(progress: CacheProgress): void {
    this.downloadListeners.forEach((callback) => callback(progress));
  }

  // Download and cache essential libraries
  async downloadEssentialLibraries(): Promise<boolean> {
    try {
      const totalSize = this.getTotalDownloadSize();

      // Check storage space
      const hasSpace = await StorageManager.hasEnoughSpace(totalSize);
      if (!hasSpace) {
        logger.warn('Insufficient storage space for caching libraries');
        this.notifyProgress({
          status: 'error',
          message: 'Storage is insufficient. Skipping caching.',
          progress: 0,
          total: ESSENTIAL_LIBRARIES.length,
          current: 0,
        });
        return false;
      }

      this.notifyProgress({
        status: 'downloading',
        message: 'Downloading essential libraries...',
        progress: 0,
        total: ESSENTIAL_LIBRARIES.length,
        current: 0,
      });

      let downloadedCount = 0;
      const sortedLibraries = [...ESSENTIAL_LIBRARIES].sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });

      for (const library of sortedLibraries) {
        try {
          this.notifyProgress({
            status: 'downloading',
            message: `Downloading essential library ${downloadedCount + 1} of ${ESSENTIAL_LIBRARIES.length}...`,
            progress: (downloadedCount / ESSENTIAL_LIBRARIES.length) * 100,
            total: ESSENTIAL_LIBRARIES.length,
            current: downloadedCount,
          });

          // message: `Downloading ${library.name}...`,

          const module = await library.importFunction();
          this.cache.set(library.name, module);
          downloadedCount++;

          logger.log(`Successfully cached library: ${library.name}`);
        } catch (error) {
          logger.error(`Failed to cache library ${library.name}:`, error);
          // Continue with other libraries even if one fails
        }
      }

      // Update cache version
      localStorage.setItem(STORAGE_KEY, CACHE_VERSION);

      this.notifyProgress({
        status: 'completed',
        message: `${downloadedCount} libraries များကို အောင်မြင်စွာ cache လုပ်ပြီးပါပြီ။`,
        progress: 100,
        total: ESSENTIAL_LIBRARIES.length,
        current: downloadedCount,
      });

      return downloadedCount > 0;
    } catch (error) {
      logger.error('Failed to download essential libraries:', error);
      this.notifyProgress({
        status: 'error',
        message: 'Libraries download လုပ်ရာတွင် အမှားတစ်ခု ဖြစ်ပွားခဲ့သည်။',
        progress: 0,
        total: ESSENTIAL_LIBRARIES.length,
        current: 0,
      });
      return false;
    }
  }

  // Get cached library
  getCachedLibrary(name: string): unknown | null {
    return this.cache.get(name) || null;
  }

  // Check if library is cached
  isLibraryCached(name: string): boolean {
    return this.cache.has(name);
  }

  // Get QR code library with fallback
  async getQRCodeLibrary(): Promise<unknown> {
    // Try to get from cache first
    let qrLibrary = this.getCachedLibrary('qrcode.react');
    if (qrLibrary) {
      return qrLibrary;
    }

    // Fallback to dynamic import
    try {
      qrLibrary = await import('qrcode.react');
      this.cache.set('qrcode.react', qrLibrary);
      return qrLibrary;
    } catch (error) {
      logger.error('Failed to load QR code library:', error);
      throw error;
    }
  }

  // Get QR code styling library with fallback
  async getQRCodeStylingLibrary(): Promise<unknown> {
    // Try to get from cache first
    let qrStylingLibrary = this.getCachedLibrary('qr-code-styling');
    if (qrStylingLibrary) {
      return qrStylingLibrary;
    }

    // Fallback to dynamic import
    try {
      qrStylingLibrary = await import('qr-code-styling');
      this.cache.set('qr-code-styling', qrStylingLibrary);
      return qrStylingLibrary;
    } catch (error) {
      logger.error('Failed to load QR code styling library:', error);
      throw error;
    }
  }

  // Get ColorThief library with fallback
  async getColorThiefLibrary(): Promise<unknown> {
    // Try to get from cache first
    let colorThiefLibrary = this.getCachedLibrary('colorthief');
    if (colorThiefLibrary) {
      return colorThiefLibrary;
    }

    // Fallback to dynamic import
    try {
      colorThiefLibrary = await import('colorthief');
      this.cache.set('colorthief', colorThiefLibrary);
      return colorThiefLibrary;
    } catch (error) {
      logger.error('Failed to load ColorThief library:', error);
      throw error;
    }
  }

  // Clear cache
  clearCache(): void {
    this.cache.clear();
    localStorage.removeItem(STORAGE_KEY);
    logger.log('Library cache cleared');
  }

  // Get cache statistics
  getCacheStats(): CacheStats {
    const cachedLibraries = Array.from(this.cache.keys());
    const totalCachedSize = cachedLibraries.reduce((total, name) => {
      const lib = ESSENTIAL_LIBRARIES.find((l) => l.name === name);
      return total + (lib?.size || 0);
    }, 0);

    return {
      cachedCount: cachedLibraries.length,
      totalCount: ESSENTIAL_LIBRARIES.length,
      cachedSize: totalCachedSize,
      totalSize: this.getTotalDownloadSize(),
      cachedLibraries,
      version: CACHE_VERSION,
    };
  }
}

// Types
export interface CacheProgress {
  status: 'downloading' | 'completed' | 'error';
  message: string;
  progress: number; // 0-100
  total: number;
  current: number;
}

export interface CacheStats {
  cachedCount: number;
  totalCount: number;
  cachedSize: number;
  totalSize: number;
  cachedLibraries: string[];
  version: string;
}

// Export singleton instance
export const libraryCacheManager = LibraryCacheManager.getInstance();

// Export storage utilities
export { StorageManager };
